/**
 * TelephonyPage Component (TASK-327, Epic 29/30 — Telephony)
 *
 * Organization phone-number pool. The org sees ONLY its own numbers
 * (server-side RBAC), orders new ones via a request dialog (fulfilled manually
 * by the platform owner), and assigns active numbers to its operators — the
 * operator's voice-channel `phone_number` is synced server-side, so the
 * outgoing caller-ID always matches the operator's incoming number.
 *
 * Visual layer follows the redesigned list pattern (DatasetsPage): status as a
 * colored dot + text, primary blue action button, dialogs for order/assign.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertCircle,
  Loader2,
  PhoneCall,
  Plus,
  UserMinus,
  UserPlus,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { StatusDot, type StatusDotColor } from '@/components/ui/status-dot';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { phoneNumbersApi } from '@/services/api/phoneNumbers';
import { usersApi } from '@/services/api/users';
import type { OrgPhoneNumber, PhoneNumberStatus } from '@/types/phoneNumber';

const STATUS_LABELS: Record<PhoneNumberStatus, string> = {
  requested: 'В обработке',
  active: 'Активен',
};

// Status rendered as a colored dot + text (shared StatusDot).
const STATUS_COLORS: Record<PhoneNumberStatus, StatusDotColor> = {
  requested: 'yellow',
  active: 'green',
};

export function TelephonyPage(): JSX.Element {
  const queryClient = useQueryClient();

  // "Заказать номер" dialog state.
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const [orderNote, setOrderNote] = useState('');

  // "Назначить оператору" dialog state (the number being assigned).
  const [assignTarget, setAssignTarget] = useState<OrgPhoneNumber | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');

  useEffect(() => {
    document.title = 'Телефония — номера - SMETA AI';
  }, []);

  const {
    data: numbers,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['phone-numbers'],
    queryFn: phoneNumbersApi.list,
  });

  // Org users for the assign select. Fetched lazily — only when the assign
  // dialog is actually opened.
  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    enabled: assignTarget != null,
  });
  const operators = useMemo(
    () => (usersData?.results ?? []).filter((u) => u.is_active),
    [usersData]
  );

  const invalidatePool = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['phone-numbers'] });
  }, [queryClient]);

  const orderMutation = useMutation({
    mutationFn: (note: string) => phoneNumbersApi.request({ note }),
    onSuccess: () => {
      invalidatePool();
      setIsOrderOpen(false);
      setOrderNote('');
      toast.success('Заявка на номер отправлена');
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err, 'Не удалось отправить заявку'));
    },
  });

  const assignMutation = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      phoneNumbersApi.assign(id, userId),
    onSuccess: () => {
      invalidatePool();
      setAssignTarget(null);
      setSelectedUserId('');
      toast.success('Номер назначен оператору');
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err, 'Не удалось назначить номер'));
    },
  });

  const unassignMutation = useMutation({
    mutationFn: (id: string) => phoneNumbersApi.unassign(id),
    onSuccess: () => {
      invalidatePool();
      toast.success('Номер снят с оператора');
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err, 'Не удалось снять номер'));
    },
  });

  const handleAssignSubmit = useCallback(() => {
    if (!assignTarget || !selectedUserId) return;
    assignMutation.mutate({ id: assignTarget.id, userId: selectedUserId });
  }, [assignTarget, selectedUserId, assignMutation]);

  const pool = numbers ?? [];

  const formatDate = useMemo(
    () => (value: string) =>
      new Date(value).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short',
      }),
    []
  );

  return (
    <>
      <PageLayout
        data-testid="telephony-page"
        title="Телефония"
        subtitle={
          <span className="hidden sm:inline">
            Пул телефонных номеров организации
          </span>
        }
        titleIcon={
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <PhoneCall className="h-5 w-5 text-white" />
          </div>
        }
        titleAddon={
          <span
            data-testid="total-count"
            className="hidden sm:inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold"
          >
            {pool.length}
          </span>
        }
        breadcrumbs={[{ label: 'Главная', href: '/' }, { label: 'Телефония' }]}
        actions={
          <Button
            data-testid="order-number-button"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => {
              setOrderNote('');
              setIsOrderOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">Заказать номер</span>
          </Button>
        }
      >
        {isLoading && (
          <div
            data-testid="telephony-loading"
            className="flex items-center justify-center py-12"
          >
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        )}

        {isError && (
          <div
            data-testid="telephony-error"
            className="flex flex-col items-center justify-center py-12"
          >
            <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Ошибка загрузки номеров
            </h3>
            <p className="text-gray-500 mb-4">
              {error instanceof Error ? error.message : 'Произошла ошибка'}
            </p>
            <Button data-testid="retry-button" onClick={() => refetch()}>
              Повторить
            </Button>
          </div>
        )}

        {!isLoading && !isError && pool.length === 0 && (
          <div
            data-testid="telephony-empty"
            className="flex flex-col items-center justify-center py-12"
          >
            <PhoneCall className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Номеров пока нет
            </h3>
            <p className="text-gray-500 mb-4">
              Закажите первый номер — заявку исполнит владелец платформы
            </p>
          </div>
        )}

        {!isLoading && !isError && pool.length > 0 && (
          <div className="bg-white rounded-lg border">
            <Table aria-label="Phone numbers table">
              <TableHeader>
                <TableRow>
                  <TableHead>Номер</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Назначен</TableHead>
                  <TableHead>Пожелания</TableHead>
                  <TableHead>Дата</TableHead>
                  <TableHead>Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pool.map((number) => (
                  <TableRow
                    key={number.id}
                    data-testid={`phone-number-row-${number.id}`}
                  >
                    <TableCell className="font-medium">
                      {number.phone_number ? (
                        number.phone_number
                      ) : (
                        <span className="italic text-gray-400">
                          — в обработке
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusDot
                        color={STATUS_COLORS[number.status]}
                        label={STATUS_LABELS[number.status]}
                      />
                    </TableCell>
                    <TableCell className="text-sm text-gray-700">
                      {number.assigned_to ? (
                        number.assigned_to.display_name
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[240px] truncate text-sm text-gray-500">
                      {number.note}
                    </TableCell>
                    <TableCell className="text-sm text-slate-400">
                      {formatDate(number.created_at)}
                    </TableCell>
                    <TableCell>
                      {number.status === 'active' && (
                        <div className="flex items-center gap-2">
                          <Button
                            data-testid={`assign-button-${number.id}`}
                            variant="outline"
                            size="sm"
                            disabled={assignMutation.isPending}
                            onClick={() => {
                              setSelectedUserId(
                                number.assigned_to?.id ?? ''
                              );
                              setAssignTarget(number);
                            }}
                          >
                            <UserPlus className="h-4 w-4 sm:mr-1" />
                            <span className="hidden sm:inline">
                              Назначить оператору
                            </span>
                          </Button>
                          {number.assigned_to && (
                            <Button
                              data-testid={`unassign-button-${number.id}`}
                              variant="outline"
                              size="sm"
                              disabled={unassignMutation.isPending}
                              onClick={() =>
                                unassignMutation.mutate(number.id)
                              }
                            >
                              <UserMinus className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline">Снять</span>
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </PageLayout>

      {/* Order number dialog */}
      <Dialog open={isOrderOpen} onOpenChange={setIsOrderOpen}>
        <DialogContent data-testid="order-number-dialog">
          <DialogHeader>
            <DialogTitle>Заказать номер</DialogTitle>
            <DialogDescription>
              Заявку исполнит владелец платформы. Номер появится в пуле со
              статусом «В обработке», после покупки станет активным.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="order-note">Пожелания</Label>
            <Textarea
              id="order-note"
              data-testid="order-note-input"
              rows={4}
              placeholder="Город, красивый номер и т.п."
              value={orderNote}
              onChange={(e) => setOrderNote(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOrderOpen(false)}>
              Отмена
            </Button>
            <Button
              data-testid="order-number-submit"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => orderMutation.mutate(orderNote)}
              disabled={orderMutation.isPending}
            >
              {orderMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Отправить заявку
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign to operator dialog */}
      <Dialog
        open={assignTarget != null}
        onOpenChange={(open) => {
          if (!open) {
            setAssignTarget(null);
            setSelectedUserId('');
          }
        }}
      >
        <DialogContent data-testid="assign-number-dialog">
          <DialogHeader>
            <DialogTitle>Назначить оператору</DialogTitle>
            <DialogDescription>
              Номер {assignTarget?.phone_number} будет назначен выбранному
              оператору: его voice-канал получит этот номер для входящих и
              исходящих звонков.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label>Оператор</Label>
            {isUsersLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Загрузка пользователей...</span>
              </div>
            ) : (
              <Select
                value={selectedUserId}
                onValueChange={setSelectedUserId}
              >
                <SelectTrigger data-testid="assign-user-select">
                  <SelectValue placeholder="Выберите оператора" />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((operator) => (
                    <SelectItem key={operator.id} value={operator.id}>
                      {operator.full_name || operator.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAssignTarget(null);
                setSelectedUserId('');
              }}
            >
              Отмена
            </Button>
            <Button
              data-testid="assign-number-submit"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleAssignSubmit}
              disabled={!selectedUserId || assignMutation.isPending}
            >
              {assignMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Назначить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TelephonyPage;
